// 3-step builder: Setup → Discover → Activate. The Discover step offers TWO ways to define a
// strategy's conditions (a Scan|Manual toggle in StepDiscover): auto-scan the best combination,
// or pick conditions by hand. Either way the user ends with an assembled long/short strategy and
// backtests it before continuing.

import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { StepProgress, type WizardStep } from "../StepProgress";
import { Step1Setup } from "./Step1Setup";
import { StepDiscover } from "./StepDiscover";
import { Step3Activate } from "./Step3Activate";
import { useBuilderState } from "../../../state/BuilderState";

const STEPS: WizardStep[] = [
  { id: "setup", label: "Setup" },
  { id: "discover", label: "Discover" },
  { id: "activate", label: "Activate" },
];

export function WizardShell() {
  const { draft } = useBuilderState();
  const [currentIndex, setCurrentIndex] = useState(0);

  const setupReady = draft.coin.trim().length > 0 && draft.indicatorSpecs.length > 0;
  // A strategy is "selected" once either entry side has conditions — whether assembled by the
  // scan or ticked by hand in the manual picker.
  const selected = draft.longCondIndices.length > 0 || draft.shortCondIndices.length > 0;
  // Step indices: 0 Setup, 1 Discover, 2 Activate.
  const stepReady = [setupReady, selected, true][currentIndex] ?? false;
  const stepWarning = !stepReady
    ? currentIndex === 0
      ? "Pick a coin and add at least one indicator to continue."
      : currentIndex === 1
        ? "Define your strategy's conditions (scan or manual) to continue."
        : null
    : null;

  // The Discover step (index 1) uses a wide list + chart split, so give it a much wider
  // container; the form-style Setup/Activate steps stay narrow for readability. Keeping the
  // width on the shared root means the progress bar and Back/Continue nav stay aligned with
  // whatever content width the current step uses.
  const wide = currentIndex === 1;

  return (
    <div className={cn("flex flex-col gap-5 w-full mx-auto", wide ? "max-w-[2000px]" : "max-w-5xl")}>
      <div className="pt-2">
        <StepProgress steps={STEPS} currentIndex={currentIndex} onStepClick={setCurrentIndex} />
      </div>

      <div>
        {currentIndex === 0 && <Step1Setup />}
        {currentIndex === 1 && <StepDiscover onAdvance={() => setCurrentIndex(2)} />}
        {currentIndex === 2 && <Step3Activate />}
      </div>

      {currentIndex < STEPS.length - 1 && (
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
          <button
            type="button"
            onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
            disabled={currentIndex === 0}
            className="flex items-center gap-2 py-2 px-4 rounded-xl font-semibold text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
          <div className="flex items-center gap-3">
            {stepWarning && (
              <span className="text-xs text-muted-foreground italic">{stepWarning}</span>
            )}
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => Math.min(STEPS.length - 1, i + 1))}
              disabled={!stepReady}
              className="flex items-center gap-2 py-2.5 px-5 rounded-xl font-bold text-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Continue
              <ArrowRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
